import axios, { AxiosError } from 'axios';
import { ImageAnalysisResult } from '../types/onboarding';
import { getLocalizedBreedArray, getLocalizedAnalysisErrorMessage } from '../utils/breedLocalization';
import { withHealthMonitoring, aiAnalysisHealth } from './aiAnalysisHealth';
import { aiAnalysisCircuitBreaker } from './circuitBreaker';
import { auth } from '../firebase';

const BACKEND_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001')
  : '';

// Configuration for stability improvements
const API_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  timeout: 60000,
  rateLimitDelay: 2000,
};

// Request queue to prevent concurrent API calls
let isProcessing = false;
const requestQueue: Array<() => Promise<void>> = [];
let lastRequestTime = 0;

/**
 * Rate limiting helper
 */
const enforceRateLimit = async (): Promise<void> => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < API_CONFIG.rateLimitDelay) {
    const delay = API_CONFIG.rateLimitDelay - timeSinceLastRequest;
    console.log(`⏳ Rate limiting: waiting ${delay}ms before next request`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  lastRequestTime = Date.now();
};

/**
 * Get Firebase ID token for backend authentication
 */
const getAuthToken = async (): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }
  return await currentUser.getIdToken();
};

/**
 * Exponential backoff with jitter
 */
const calculateBackoffDelay = (attempt: number): number => {
  const exponentialDelay = API_CONFIG.baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return Math.min(exponentialDelay + jitter, API_CONFIG.maxDelay);
};

/**
 * Retry wrapper with exponential backoff
 */
const withRetry = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = API_CONFIG.maxRetries
): Promise<T> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const delay = calculateBackoffDelay(attempt - 1);
        console.log(`🔄 Retry attempt ${attempt}/${maxRetries} for ${operationName} after ${delay}ms delay`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      return await operation();
    } catch (error) {
      lastError = error as Error;
      console.warn(`❌ Attempt ${attempt + 1}/${maxRetries + 1} failed for ${operationName}:`, error);

      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          console.error(`🚫 Non-retryable error (${status}) for ${operationName}`);
          throw error;
        }
      }

      if (attempt === maxRetries) {
        throw lastError;
      }
    }
  }

  throw lastError || new Error(`Failed after ${maxRetries + 1} attempts`);
};

/**
 * Queue management for concurrent requests
 */
const processQueue = async (): Promise<void> => {
  if (isProcessing || requestQueue.length === 0) return;

  isProcessing = true;

  while (requestQueue.length > 0) {
    const operation = requestQueue.shift();
    if (operation) {
      try {
        await operation();
      } catch (error) {
        console.error('❌ Error in queued operation:', error);
      }
    }
  }

  isProcessing = false;
};

/**
 * Analyzes a pet image using backend API (which proxies to OpenAI Vision)
 * @param imageUrl - Firebase Storage URL of the pet image
 * @param petType - Type of pet ('dog' or 'cat')
 * @param userLanguage - User's preferred language for response
 * @returns Promise<ImageAnalysisResult> - Analysis results with confidence scores
 */
export const analyzePetImage = async (
  imageUrl: string,
  petType: 'dog' | 'cat' | 'other',
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en'
): Promise<ImageAnalysisResult> => {
  // Check if service is healthy before proceeding
  if (!aiAnalysisHealth.isHealthy()) {
    console.warn('⚠️ AI Analysis service is unhealthy, using fallback result');
    return createFallbackAnalysisResult(petType, userLanguage, imageUrl);
  }

  // Check circuit breaker
  if (!aiAnalysisCircuitBreaker.canExecute()) {
    console.warn('⚠️ Circuit breaker is open, using fallback result');
    return createFallbackAnalysisResult(petType, userLanguage, imageUrl);
  }

  return withHealthMonitoring(async () => {
    return aiAnalysisCircuitBreaker.execute(async () => {
      return new Promise<ImageAnalysisResult>((resolve, reject) => {
        const operation = async () => {
          try {
            console.log('🖼️ Starting pet image analysis via backend...');

            // Enforce rate limiting
            await enforceRateLimit();

            // Get Firebase auth token
            const idToken = await getAuthToken();

            console.log('📸 Sending request to backend:', {
              endpoint: `${BACKEND_URL}/api/ai/analyze`,
              petType,
              language: userLanguage
            });

            const startTime = Date.now();

            // Call backend API instead of OpenAI directly
            const response = await withRetry(
              () => axios.post(
                `${BACKEND_URL}/api/ai/analyze`,
                {
                  imageUrl,
                  petType,
                  language: userLanguage
                },
                {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                  },
                  timeout: API_CONFIG.timeout
                }
              ),
              'Backend AI analyze call'
            );

            const responseTime = Date.now() - startTime;
            console.log(`✅ Backend analysis completed in ${responseTime}ms`);

            const analysisData = response.data.data.analysis;

            // Convert response format to match expected ImageAnalysisResult
            const result: ImageAnalysisResult = {
              breed: analysisData.breed,
              confidence: analysisData.breedConfidence / 100, // Backend returns 0-100, convert to 0-1
              age: analysisData.age,
              ageConfidence: analysisData.ageConfidence / 100,
              sex: analysisData.sex,
              sexConfidence: analysisData.sexConfidence / 100,
              description: analysisData.description,
              analysisImageUrl: imageUrl
            };

            console.log('✅ Pet image analysis completed successfully');
            resolve(result);

          } catch (error) {
            console.error('❌ Error analyzing pet image:', error);

            // Enhanced error handling with specific error types
            if (error instanceof AxiosError) {
              const status = error.response?.status;
              const statusText = error.response?.statusText;
              const errorData = error.response?.data;

              console.error('🌐 Backend API Error details:', {
                status,
                statusText,
                data: errorData,
                message: error.message
              });

              let errorMessage: string;
              switch (status) {
                case 429:
                  errorMessage = 'Rate limit exceeded. Please try again in a moment.';
                  break;
                case 400:
                  errorMessage = 'Invalid image or request format. Please try a different image.';
                  break;
                case 401:
                  errorMessage = 'Authentication failed. Please sign in again.';
                  break;
                case 403:
                  errorMessage = 'Access forbidden. Please check your permissions.';
                  break;
                case 500:
                case 502:
                case 503:
                case 504:
                  errorMessage = 'Server error. Please try again in a moment.';
                  break;
                default:
                  errorMessage = `Request failed (${status}). Please try again.`;
              }

              reject(new Error(errorMessage));
              return;
            }

            // Handle timeout errors
            if (error instanceof Error && error.message.includes('timeout')) {
              reject(new Error('Request timed out. Please try again with a smaller image or better connection.'));
              return;
            }

            // Handle network errors
            if (error instanceof Error && error.message.includes('Network Error')) {
              reject(new Error('Network error. Please check your internet connection and try again.'));
              return;
            }

            // Generic error fallback
            reject(new Error('Failed to analyze pet image. Please try again.'));
          }
        };

        // Add to queue and process
        requestQueue.push(operation);
        processQueue();
      });
    });
  }, 'Pet Image Analysis');
};

/**
 * Validates if an image file is suitable for analysis
 * @param file - Image file to validate
 * @returns boolean - Whether the file is valid
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Please upload a JPEG, PNG, or WebP image file.'
    };
  }

  const maxSize = 20 * 1024 * 1024; // 20MB
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'Image file size must be less than 20MB.'
    };
  }

  const minSize = 1024; // 1KB
  if (file.size < minSize) {
    return {
      valid: false,
      error: 'Image file appears to be corrupted or too small.'
    };
  }

  return { valid: true };
};

/**
 * Creates a fallback analysis result when AI analysis fails
 * @param petType - Type of pet ('dog' or 'cat')
 * @param userLanguage - User's preferred language
 * @param imageUrl - URL of the analyzed image
 * @returns ImageAnalysisResult - Fallback result
 */
export const createFallbackAnalysisResult = (
  petType: 'dog' | 'cat' | 'other',
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en',
  imageUrl: string
): ImageAnalysisResult => {
  const randomSex = Math.random() < 0.5 ? 'male' : 'female';
  const sexConfidence = Math.random() * 0.3 + 0.5;
  const age = Math.random() * 4 + 1;

  return {
    breed: 'Mixed Breed',
    confidence: 0.25,
    age: age,
    ageConfidence: 0.3,
    sex: randomSex,
    sexConfidence: sexConfidence,
    description: getLocalizedAnalysisErrorMessage(userLanguage),
    analysisImageUrl: imageUrl
  };
};

/**
 * Checks if the API is currently processing requests
 * @returns boolean - Whether the API is busy
 */
export const isApiBusy = (): boolean => {
  return isProcessing || requestQueue.length > 0;
};

/**
 * Gets the current queue length
 * @returns number - Number of pending requests
 */
export const getQueueLength = (): number => {
  return requestQueue.length;
};

/**
 * Clears the request queue (use with caution)
 */
export const clearQueue = (): void => {
  requestQueue.length = 0;
  isProcessing = false;
};

/**
 * Gets comprehensive service status including health and circuit breaker
 */
export const getServiceStatus = () => {
  return {
    health: aiAnalysisHealth.getHealthStatus(),
    circuitBreaker: aiAnalysisCircuitBreaker.getStatus(),
    queue: {
      isProcessing,
      queueLength: requestQueue.length,
      lastRequestTime
    },
    backend: {
      url: BACKEND_URL,
      configured: !!BACKEND_URL
    }
  };
};
